import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import ButtonBack from "../utils/ButtonBack";
import { startPayment, refreshOrgPlan, verifyOrder } from "../utils/paymentGateway";

const PLAN_ORDER = ["FREE", "BASIC", "PRO"];
const PLAN_META = {
  FREE: { name: "Free", tint: "#F1F4F8", ink: "#3D4C60", dot: "#8A97A8" },
  BASIC: { name: "Basic", tint: "#EAF2FB", ink: "#1B4F82", dot: "#1F6FEB" },
  PRO: { name: "Pro", tint: "#EFEDFB", ink: "#4A3FBF", dot: "#6C5CE7" },
};
const FEATURE_LABELS = { LEAD: "Leads, reminders & inventory", LEAD_STRUCTURE: "Lead structure", LEAD_CUSTOM_FIELD: "Custom fields" };
const featureLabel = (k) => FEATURE_LABELS[k] || String(k || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
const rank = (p) => PLAN_ORDER.indexOf(String(p || "FREE").toUpperCase());
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
// Superadmin Limits table → { PLAN: { leadLimit, projectLimit, monthlyPrice, yearlyPrice } }
const fetchPlanLimits = async (token) => {
  const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription-limit`, authHeaders(token));
  const out = {};
  (Array.isArray(res.data) ? res.data : []).forEach((l) => { out[l.type] = l; });
  return out;
};
// Superadmin Features table → { PLAN: [featureKey] }, cascaded PRO ⊇ BASIC ⊇ FREE (same rule as fetchFeatures.js)
const fetchPlanMatrix = async (token) => {
  const rows = await Promise.all(PLAN_ORDER.map((p) =>
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription-modal?type=${p}`, authHeaders(token))
      .then((r) => (Array.isArray(r.data) ? r.data.map((f) => f.feature) : [])).catch(() => [])));
  const out = {}; const acc = [];
  PLAN_ORDER.forEach((p, i) => { rows[i].forEach((k) => { if (!acc.includes(k)) acc.push(k); }); out[p] = [...acc]; });
  return out;
};

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const GST_FALLBACK = 18;

const cardSx = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "16px" };
const label = { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: "#66748A" };

// /dashboard/checkout?plan=BASIC|PRO&feature=LEAD_STRUCTURE
const Checkout = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));
  const subs = String(JSON.parse(localStorage.getItem("subs")) || "FREE").toUpperCase();
  const requested = String(params.get("plan") || "PRO").toUpperCase();
  const [plan, setPlan] = useState(PLAN_META[requested] ? requested : "PRO");
  const [cycle, setCycle] = useState("MONTHLY");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [prices, setPrices] = useState({}); // { PRO: { monthlyPrice, yearlyPrice } } from the superadmin Limits table
  const [matrix, setMatrix] = useState({});  // { PRO: [featureKey] } from the superadmin Features table
  const [gstPercent, setGstPercent] = useState(GST_FALLBACK);
  const feature = params.get("feature");

  useEffect(() => {
    if (!storedUser?.accessToken) return;
    const headers = { Authorization: `Bearer ${storedUser.accessToken}` };
    const base = process.env.REACT_APP_BACKEND_URL;
    fetchPlanLimits(storedUser.accessToken).then(setPrices).catch(() => {});
    fetchPlanMatrix(storedUser.accessToken).then(setMatrix).catch(() => {});
    axios.get(`${base}/payment/config`, { headers })
      .then((res) => { if (res.data?.gstPercent != null) setGstPercent(Number(res.data.gstPercent)); })
      .catch(() => {});
  }, []);

  // Price for a plan/cycle, as set by the superadmin. 0 = not configured.
  const priceOf = (p, c) => {
    const row = prices[p];
    const v = c === "YEARLY" ? row?.yearlyPrice : row?.monthlyPrice;
    return v != null && Number(v) > 0 ? Number(v) : 0;
  };

  const meta = PLAN_META[plan];
  const base = priceOf(plan, cycle);
  const tax = Math.round((base * gstPercent) / 100);
  const total = base + tax;
  const yearlySaving = priceOf(plan, "MONTHLY") * 12 - priceOf(plan, "YEARLY");
  const options = useMemo(() => PLAN_ORDER.filter((p) => rank(p) > rank(subs)), [subs]);
  const priceMissing = base <= 0;

  const pay = async () => {
    setError("");
    setNotice("");
    setPaying(true);
    try {
      await startPayment({
        orgId, plan, cycle,
        token: storedUser?.accessToken,
        // Success or failure → the result page checks the order and shows the right screen.
        onComplete: (orderId) =>
          navigate(`/dashboard/payment/result?order_id=${encodeURIComponent(orderId)}`, { replace: true }),
        // Closed without paying → stay here with a neutral note.
        onDismiss: (msg) => {
          setPaying(false);
          setNotice(msg || "Payment cancelled. You have not been charged.");
        },
      });
    } catch (e) {
      setPaying(false);
      setError(e?.response?.data?.message || e?.message || "Could not start the payment.");
    }
  };

  return (
    <div style={{ fontFamily: FONT, color: INK, textAlign: "left" }}>
      <ButtonBack />
      <Typography sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mt: 1, mb: 0.5 }}>
        Upgrade to {meta.name}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: "#4A5A6E", mb: 2.5, textWrap: "pretty" }}>
        {feature && FEATURE_LABELS[feature] ? `${FEATURE_LABELS[feature]} is included in ${meta.name}. ` : ""}
        The plan applies to this organisation only. Your leads, users and settings stay as they are.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" }, gap: 2, alignItems: "start", maxWidth: 980 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ ...cardSx, p: "18px 20px" }}>
            <div style={label}>Plan</div>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.25, mt: 1.25 }}>
              {options.map((p) => {
                const m = PLAN_META[p];
                const on = p === plan;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlan(p)}
                    style={{ textAlign: "left", padding: "13px 14px", background: on ? m.tint : "#FFFFFF", border: `1.5px solid ${on ? m.dot : LINE}`, borderRadius: 12, cursor: "pointer", fontFamily: FONT, color: INK }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.02em" }}>{m.name}</span>
                      <span style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: m.ink }}>{formatINR(priceOf(p, "MONTHLY"))}/mo</span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12.5, color: "#4A5A6E", lineHeight: 1.45 }}>
                      {(matrix[p] || []).map(featureLabel).join(" · ") || "\u00a0"}
                    </div>
                  </button>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ ...cardSx, p: "18px 20px" }}>
            <div style={label}>Billing</div>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 1.25 }}>
              {[
                { id: "MONTHLY", title: "Monthly", sub: `${formatINR(priceOf(plan, "MONTHLY"))} every month` },
                { id: "YEARLY", title: "Yearly", sub: `${formatINR(priceOf(plan, "YEARLY"))} every year${yearlySaving > 0 ? ` · save ${formatINR(yearlySaving)}` : ""}` },
              ].map((c) => {
                const on = cycle === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCycle(c.id)}
                    style={{ flex: "1 1 200px", textAlign: "left", padding: "12px 14px", background: on ? "#EAF2FB" : "#FFFFFF", border: `1.5px solid ${on ? PRIMARY : LINE}`, borderRadius: 12, cursor: "pointer", fontFamily: FONT, color: INK }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{c.title}</div>
                    <div style={{ marginTop: 3, fontSize: 12.5, color: "#4A5A6E" }}>{c.sub}</div>
                  </button>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, p: "18px 20px", position: { md: "sticky" }, top: { md: 88 } }}>
          <div style={label}>Order summary</div>
          <div style={{ display: "grid", gap: 8, marginTop: 12, fontSize: 13.5 }}>
            <Row k={`${meta.name} · ${cycle.toLowerCase()}`} v={formatINR(base)} />
            <Row k={`GST ${gstPercent}%`} v={formatINR(tax)} />
            <div style={{ height: 1, background: LINE, margin: "4px 0" }} />
            <Row k="Total due today" v={formatINR(total)} strong />
          </div>
          {error && (
            <p style={{ margin: "12px 0 0", padding: "9px 11px", background: "#FDECEC", border: "1px solid #F5C2C2", borderRadius: 9, fontSize: 12.5, color: "#B42323" }}>{error}</p>
          )}
          {notice && !error && (
            <p style={{ margin: "12px 0 0", padding: "9px 11px", background: "#F4F7FA", border: `1px solid ${LINE}`, borderRadius: 9, fontSize: 12.5, color: "#3D4C60" }}>{notice}</p>
          )}
          {priceMissing && !error && (
            <p style={{ margin: "12px 0 0", padding: "9px 11px", background: "#FFF6E5", border: "1px solid #F3D9A4", borderRadius: 9, fontSize: 12.5, color: "#8A5A00" }}>
              Pricing for this plan hasn't been set yet. Please contact support.
            </p>
          )}
          <Button
            onClick={pay}
            disabled={paying || priceMissing}
            sx={{ mt: 2, width: "100%", height: 44, borderRadius: "10px", background: PRIMARY, color: "#FFFFFF", fontFamily: FONT, fontSize: 14, fontWeight: 700, textTransform: "none", boxShadow: "none", "&:hover": { background: "#084767" }, "&.Mui-disabled": { background: "#B4BFCC", color: "#FFFFFF" } }}
          >
            {paying ? "Opening payment…" : `Pay ${formatINR(total)}`}
          </Button>
          <Button
            onClick={() => navigate(-1)}
            sx={{ mt: 1, width: "100%", height: 40, borderRadius: "10px", border: `1px solid ${FIELD_EDGE}`, background: "#FFFFFF", color: INK, fontFamily: FONT, fontSize: 13.5, fontWeight: 600, textTransform: "none", "&:hover": { background: "#F4F7FA" } }}
          >
            Cancel
          </Button>
          <p style={{ margin: "12px 0 0", fontSize: 12, lineHeight: 1.5, color: "#66748A", textWrap: "pretty" }}>
            Paid securely via Cashfree (UPI, cards, net banking). Your plan activates as soon as the payment is confirmed.
          </p>
        </Box>
      </Box>
    </div>
  );
};

const Row = ({ k, v, strong }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
    <span style={{ color: strong ? INK : "#4A5A6E", fontWeight: strong ? 700 : 500 }}>{k}</span>
    <span style={{ fontFamily: MONO, fontWeight: strong ? 700 : 600, fontSize: strong ? 15 : 13.5 }}>{v}</span>
  </div>
);

// /dashboard/payment/result?order_id=CRM_xxx
// Used after the Cashfree popup AND as Cashfree's return_url. Always asks the backend for the real status.
const RESULT_TONE = {
  success: { bg: "#E9F7EE", edge: "#C4E8D2", ink: "#1B6B41", solid: "#2FA864", icon: "✓", label: "Payment successful" },
  pending: { bg: "#FFF6E5", edge: "#F3D9A4", ink: "#8A5A00", solid: "#E0A21B", icon: "…", label: "Confirming payment" },
  failed: { bg: "#FBEDEB", edge: "#F2CFCA", ink: "#B42323", solid: "#E0564A", icon: "✕", label: "Payment failed" },
};

export const PaymentResult = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("order");
  const [state, setState] = useState(orderId ? "checking" : "failed"); // checking | success | pending | failed
  const [info, setInfo] = useState({ plan: "", endDate: null, message: "" });

  useEffect(() => {
    if (!orderId) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const token = storedUser?.accessToken;
    let cancelled = false;

    // Bank confirmation can lag a few seconds: re-check up to ~30s while PENDING.
    const check = async (attempt = 0) => {
      const data = await verifyOrder({ orderId, token });
      if (cancelled) return;
      const plan = String(data?.plan || "").toUpperCase();
      if (data?.status === "ACTIVATED") {
        // Save the new plan and tell the navbar + sidebar to update right away (no reload).
        if (plan) localStorage.setItem("subs", JSON.stringify(plan));
        window.dispatchEvent(new Event("plan-changed"));
        setInfo({ plan, endDate: data.subscriptionEndDate, message: "" });
        setState("success");
      } else if (data?.status === "PENDING" && attempt < 10) {
        setInfo((i) => ({ ...i, plan }));
        setState("pending");
        setTimeout(() => check(attempt + 1), 3000);
      } else if (data?.status === "PENDING") {
        setInfo({ plan, endDate: null, message: "Still waiting for the bank. If money was deducted, your plan will switch on automatically within a few minutes." });
        setState("pending");
      } else {
        setInfo({ plan, endDate: null, message: data?.message || "" });
        setState("failed");
      }
    };
    check();
    return () => { cancelled = true; };
  }, [orderId]);

  if (state === "checking") {
    return (
      <div style={{ fontFamily: FONT, color: INK, display: "flex", justifyContent: "center", paddingTop: 48 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: "#4A5A6E" }}>Checking your payment…</Typography>
      </div>
    );
  }

  const t = RESULT_TONE[state];
  const planName = PLAN_META[info.plan]?.name || "your new plan";
  const until = info.endDate
    ? new Date(info.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const title = state === "success"
    ? `You're on ${planName}`
    : state === "pending"
      ? "We're confirming your payment"
      : "Payment didn't go through";

  const body = state === "success"
    ? "Your organisation has been upgraded. Locked features are open now."
    : state === "pending"
      ? info.message || "This usually takes a few seconds. Please don't pay again."
      : "If any amount was deducted, it will be refunded automatically to your original payment method.";

  const rows = [
    orderId && { k: "Order ID", v: orderId, mono: true },
    info.plan && { k: "Plan", v: planName },
    state === "success" && until && { k: "Valid until", v: until },
    state === "failed" && info.message && { k: "Reason", v: info.message },
  ].filter(Boolean);

  const btn = { height: 44, borderRadius: "10px", fontFamily: FONT, fontSize: 14, fontWeight: 700, textTransform: "none", boxShadow: "none", flex: "1 1 160px" };

  return (
    <div style={{ fontFamily: FONT, color: INK, display: "flex", justifyContent: "center", padding: "32px 0" }}>
      <Box sx={{ ...cardSx, width: "100%", maxWidth: 460, p: "32px 26px 24px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: "50%", background: t.bg, border: `1px solid ${t.edge}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: "50%", background: t.solid, color: "#FFFFFF", fontSize: 24, fontWeight: 800 }}>
            {t.icon}
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: t.ink }}>{t.label}</div>
        <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", mt: 0.75 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.55, color: "#4A5A6E", mt: 0.75, textWrap: "pretty" }}>{body}</Typography>

        {rows.length > 0 && (
          <div style={{ display: "grid", gap: 8, marginTop: 18, padding: "12px 14px", background: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: 12, textAlign: "left", fontSize: 13 }}>
            {rows.map((r) => (
              <div key={r.k} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span style={{ color: "#66748A", flex: "0 0 auto" }}>{r.k}</span>
                <span style={{ fontFamily: r.mono ? MONO : FONT, fontWeight: 600, textAlign: "right", wordBreak: "break-all" }}>{r.v}</span>
              </div>
            ))}
          </div>
        )}

        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 2.5 }}>
          {state === "failed" && (
            <Button
              onClick={() => navigate(`/dashboard/checkout?plan=${info.plan || "PRO"}`)}
              sx={{ ...btn, background: PRIMARY, color: "#FFFFFF", "&:hover": { background: "#084767" } }}
            >
              Try again
            </Button>
          )}
          <Button
            onClick={() => (window.location.href = "/dashboard")}
            sx={state === "failed"
              ? { ...btn, border: `1px solid ${FIELD_EDGE}`, background: "#FFFFFF", color: INK, fontWeight: 600 }
              : { ...btn, background: state === "success" ? "#1B6B41" : PRIMARY, color: "#FFFFFF", "&:hover": { opacity: 0.92, background: state === "success" ? "#1B6B41" : PRIMARY } }}
          >
            Go to dashboard
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default Checkout;